import { SBCAAS_CLASSIC_LABEL } from '@/constants/sbcaas';
import { resolvePartnerName } from './ownerName';
import { SRT_TRUNK_TYPE_ID } from '@/constants/srt';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { axios } from 'services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import getServiceProfiles from 'API/getServiceProfiles';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import getTrunkTypesSpPartner from 'API/ServiceProfiles/getTrunkTypesServiceProfile';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';
import config from '@/config.json';
import FormBase from '@/components/forms/new/FormBase';
import { Field } from 'formik';
import { validationSchemaSBCAAS } from './schema';
// import SBCaaSNameField from './formFields/SBCaaSNameField';
import SBCaaSVisibleScopeField from './formFields/SBCaaSVisibleScopeField';
import SBCaaSTrunkTypesField from './formFields/SBCaaSTrunkTypesField';
import SBCaaSRegionsField from './formFields/SBCaaSRegionsField';
import SBCaaSMaxChannelField from './formFields/SBCaaSMaxChannelField';
import SBCaaSMaxRedundancyField from './formFields/SBCaaSMaxRedundancyField';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';
const MAX_REDUNDANCY_DROPDOWN = [
    { name: '1', value: 1 },
    { name: '2', value: 2 },
    { name: '3', value: 3 },
    { name: '4', value: 4 },
];

const SBCAsAService = ({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { data: currentCompanyData } = useGetCurrentCompany();

    const partnerName = resolvePartnerName({
        companiesAndPartners,
        currentCompanyData,
        currentPartner: partnerID,
    });
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);
    // Removed ufElements/walkthroughIds (not used)
    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...chakraSelectDark,
    };

    const DEFAULT_SBCAAS_PAYLOAD = {
        name: resolveWholesalerProfileName(null, SBCAAS_CLASSIC_LABEL),
        maxChannel: 10,
        maxRedundancy: 2,
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        serviceType: 'SBCaaS',
        visibleScope: level === 'partner' ? 20 : 60,
        regions: [],
        trunkTypes: [],
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    SBCAAS_CLASSIC_LABEL,
                ),
            });
        } else if (mode !== 'EDIT' && level === 'partner' && partnerName) {
            handleChangeState({
                name: `${partnerName} - ${SBCAAS_CLASSIC_LABEL} Partner Profile`,
            });
        }
    }, [
        mode,
        partnerName,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    const [modalState, setModalState] = useState(
        data || DEFAULT_SBCAAS_PAYLOAD,
    );
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const scope =
        level === 'partner'
            ? `Partner/${data?.partnerID || partnerID}`
            : `Wholesaler/${data?.wholesalerID || wholesalerID}`;

    const { data: availableRegions = [], isLoading: regionsLoading } = useQuery(
        {
            queryKey: ['sbcaasRegions', scope],
            queryFn: async () => {
                const result = await axios.get(
                    `/ServiceProfiles/SBCaaS/${scope}/Regions/Available`,
                );
                return result.data.map(multiSelectDataMapper);
            },
        },
    );

    const { data: trunkTypes = [], isLoading: trunkTypesLoading } = useQuery({
        queryKey: ['sbcaasTrunkTypes', level, partnerID, wholesalerID],
        queryFn: async () => {
            if (level === 'partner') {
                const trunkResult = await getTrunkTypesSpPartner(partnerID);
                return trunkResult.data.map(multiSelectDataMapper);
            } else {
                const trunkResult = await axios.get('TrunkTypes/Available');
                return trunkResult.data.map(multiSelectDataMapper);
            }
        },
        // The wholesaler-level list is platform-agnostic and includes the
        // modern service's trunk type, which belongs to SBCaaS (Modern) alone.
        // The partner-level endpoint is already scoped, so this only ever
        // filters at wholesaler level.
        select: (types) =>
            types.filter(
                ({ id, value }) => (id ?? value) !== SRT_TRUNK_TYPE_ID,
            ),
    });

    useQuery({
        queryKey: ['sbcaasSPData', data?.id, mode],
        queryFn: () => getServiceProfiles({ level, id: data?.id }),
        enabled: mode === 'EDIT' && Boolean(data),
        onSuccess: (spResult) => {
            setModalState({
                ...spResult.data,
                regions: spResult.data.regions.map(multiSelectDataMapper),
                trunkTypes: spResult.data.trunkTypes.map(multiSelectDataMapper),
            });
        },
    });

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    if (regionsLoading || trunkTypesLoading) {
        return <LoadingIndicator />;
    }
    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };

    const initialValues = {
        name: modalState?.name || '',
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
        trunkTypes: mapToOptions(modalState?.trunkTypes, trunkTypes),
        regions: mapToOptions(modalState?.regions, availableRegions),
        maxChannel: modalState?.maxChannel ?? 10,
        maxRedundancy: modalState?.maxRedundancy ?? 2,
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        serviceType: 'SBCaaS',
    };

    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);

        const finalSubmitValues = {
            ...modalState,
            ...values,
        };
        try {
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'SBCaaS',
                    payload: finalSubmitValues,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'SBCaaS',
                    payload: values,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}
                validationSchema={validationSchemaSBCAAS}>
                {() => [
                    <Field key="visibleScope" name="visibleScope">
                        {(fieldProps) => (
                            <SBCaaSVisibleScopeField
                                {...fieldProps}
                                options={visibleScopeOptions}
                                requiredScope={requiredScope}
                                config={config}
                                darkMode={darkMode}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="trunkTypes" name="trunkTypes">
                        {(fieldProps) => (
                            <SBCaaSTrunkTypesField
                                {...fieldProps}
                                options={trunkTypes}
                                darkMode={darkMode}
                                chakraStyles={chakraStyles}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="regions" name="regions">
                        {(fieldProps) => (
                            <SBCaaSRegionsField
                                {...fieldProps}
                                options={availableRegions}
                                darkMode={darkMode}
                                chakraStyles={chakraStyles}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="maxChannel" name="maxChannel">
                        {(fieldProps) => (
                            <SBCaaSMaxChannelField
                                {...fieldProps}
                                darkMode={darkMode}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="maxRedundancy" name="maxRedundancy">
                        {(fieldProps) => (
                            <SBCaaSMaxRedundancyField
                                {...fieldProps}
                                options={MAX_REDUNDANCY_DROPDOWN}
                                darkMode={darkMode}
                                chakraStyles={chakraStyles}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    error && (
                        <Box paddingBottom="0.5rem" key="error-box-sbcaas">
                            <Alert
                                key="error"
                                status="error"
                                mt={4}
                                marginBottom="1rem">
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        </Box>
                    ),
                ]}
            </FormBase>
        </>
    );
};

export default SBCAsAService;
